import { AlertCircle, BarChart3 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TelemetryChart } from "@/components/telemetry/TelemetryChart";
import VideoSessionTimelineLane from "@/components/video/VideoSessionTimelineLane";
import type { ChartControls } from "@/hooks/useAnalyticsKeyboardShortcuts";
import type { SessionLapMarker } from "@/utils/lapTimeline";
import type {
  TimelineRange,
} from "@/utils/sharedTimeline";
import type {
  VideoSessionTimeline,
} from "@/utils/videoSessionTimeline";
import type {
  ChannelColorIndices,
} from "@/components/telemetry/channelColors";
import type {
  ChannelMetadata,
  TelemetryDataPoint,
  TelemetryLapSummary,
  TelemetryNote,
} from "@/types";

interface ChartPanelProps {
  isLoading: boolean;
  error: unknown;
  telemetryMissing: boolean;
  hasChannelsSelected: boolean;
  hasData: boolean;
  chartData: TelemetryDataPoint[];
  selectedChannels: string[];
  channelColorIndices: ChannelColorIndices;
  channelMetadata: Record<string, ChannelMetadata>;
  sessionId: string;
  syncCurrentTime: number;
  onTimeUpdate: (time: number) => void;
  onHoverTimeUpdate: (timestamp: number | null) => void;
  onGetChartControls: (controls: ChartControls | null) => void;
  autoCenter: boolean;
  onAutoCenterChange: (value: boolean) => void;
  showGrid: boolean;
  onShowGridChange: (value: boolean) => void;
  chartHeight: number;
  onChartHeightChange: (value: number) => void;
  chartViewport: { startTime: number; endTime: number } | null;
  onChartViewportChange: (
    value: { startTime: number; endTime: number } | null,
  ) => void;
  notes: TelemetryNote[];
  onNoteClick: (note: TelemetryNote) => void;
  onNoteCreate: (timeOffset: number) => void;
  onNoteUpdate: (noteId: string, timeOffset: number) => void;
  selectedNoteId?: string;
  notesEnabled: boolean;
  videoTimeline: VideoSessionTimeline | null;
  videoTimelineCurrentTime: number;
  videoTimelineViewport: TimelineRange | null;
  videoTimelineSyncOffset: number;
  onVideoTimelineTimeUpdate: (time: number) => void;
  laps: TelemetryLapSummary[];
  onLapClick: (marker: SessionLapMarker) => void;
}

export default function ChartPanel({
  isLoading,
  error,
  telemetryMissing,
  hasChannelsSelected,
  hasData,
  chartData,
  selectedChannels,
  channelColorIndices,
  channelMetadata,
  sessionId,
  syncCurrentTime,
  onTimeUpdate,
  onHoverTimeUpdate,
  onGetChartControls,
  autoCenter,
  onAutoCenterChange,
  showGrid,
  onShowGridChange,
  chartHeight,
  onChartHeightChange,
  chartViewport,
  onChartViewportChange,
  notes,
  onNoteClick,
  onNoteCreate,
  onNoteUpdate,
  selectedNoteId,
  notesEnabled,
  videoTimeline,
  videoTimelineCurrentTime,
  videoTimelineViewport,
  videoTimelineSyncOffset,
  onVideoTimelineTimeUpdate,
  laps,
  onLapClick,
}: ChartPanelProps) {
  const { t } = useTranslation(["pages", "common"]);

  return (
    <div className="h-full overflow-y-auto">
      <div className="bg-[#080810] flex flex-col min-h-full">
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center bg-[#080810]">
            <div className="text-center">
              <div className="h-7 w-7 border-2 border-[#3b82f6] border-t-transparent rounded-full mx-auto mb-4 animate-spin" />
              <p className="text-[#4b5563] text-sm">{t("pages:sessions.analytics.loadingChartData")}</p>
            </div>
          </div>
        ) : error && !telemetryMissing ? (
          <div className="flex-1 flex items-center justify-center bg-[#080810]">
            <div className="text-center">
              <AlertCircle className="h-8 w-8 text-[#f87171] mx-auto mb-4 opacity-60" />
              <p className="text-[#f87171] text-sm opacity-80">{t("pages:sessions.analytics.failedToLoadChartData")}</p>
            </div>
          </div>
        ) : !hasChannelsSelected ? (
          <div className="flex-1 flex items-center justify-center bg-[#080810]">
            <div className="text-center">
              <BarChart3 className="h-10 w-10 text-[#374151] mx-auto mb-4 opacity-40" />
              <p className="text-base font-medium text-[#4b5563] mb-2">{t("pages:sessions.analytics.selectChannels")}</p>
              <p className="text-[#374151] text-sm">{t("pages:sessions.analytics.chooseChannelsHint")}</p>
            </div>
          </div>
        ) : !hasData ? (
          <div className="flex-1 flex items-center justify-center bg-[#080810]">
            <div className="text-center">
              <AlertCircle className="h-8 w-8 text-[#374151] mx-auto mb-4 opacity-40" />
              <p className="text-base font-medium text-[#4b5563] mb-2">{t("pages:sessions.analytics.noDataAvailable")}</p>
              <p className="text-[#374151] text-sm">{t("pages:sessions.analytics.noTelemetryForSelectedChannels")}</p>
            </div>
          </div>
        ) : (
          <>
            <TelemetryChart
              data={chartData}
              selectedChannels={selectedChannels}
              channelColorIndices={channelColorIndices}
              channelMetadata={channelMetadata}
              sessionId={sessionId}
              syncCurrentTime={syncCurrentTime}
              onTimeUpdate={onTimeUpdate}
              onHoverTimeUpdate={onHoverTimeUpdate}
              onGetChartControls={onGetChartControls}
              autoCenter={autoCenter}
              onAutoCenterChange={onAutoCenterChange}
              showGrid={showGrid}
              onShowGridChange={onShowGridChange}
              chartHeight={chartHeight}
              onChartHeightChange={onChartHeightChange}
              chartViewport={chartViewport}
              onChartViewportChange={onChartViewportChange}
              notes={notes}
              onNoteClick={onNoteClick}
              onNoteCreate={onNoteCreate}
              onNoteUpdate={onNoteUpdate}
              selectedNoteId={selectedNoteId}
              notesEnabled={notesEnabled}
              className="w-full"
            />
            {videoTimeline && videoTimeline.clips.length > 0 && (
              <VideoSessionTimelineLane
                timeline={videoTimeline}
                currentTime={videoTimelineCurrentTime}
                viewport={videoTimelineViewport}
                syncOffset={videoTimelineSyncOffset}
                notes={notes}
                onTimeChange={onVideoTimelineTimeUpdate}
                onNoteClick={onNoteClick}
                onNoteCreate={onNoteCreate}
                notesEnabled={notesEnabled}
                laps={laps}
                onLapClick={onLapClick}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
